// 离线上行队列（B5 最重头：离线上行+同步冲突合并）。
//
// 架构（WebView 壳无原生 DB，全走既有 REST，不发明新协议）：
//  1. 写操作断网/网络失败时落 IndexedDB 队列（molore-outbox/ops），立刻回报
//     「已入队」——不落假结果（血泪#34 哲学），UI 明示离线排队
//  2. 联网（online 事件 + 30s 轮询 + 启动）按时间序 FIFO 重放：
//     创建类带 X-Idempotency-Key（服务端幂等去重，notes.py _idem_*）；
//     更新类带 X-Base-Updated-At（基线版本号，服务端撞版本 409 回当前行）
//  3. 409 → 移入 conflicts 仓（本地 op + 服务端当前行 + 基线），冲突中心
//     三路对照人工合并——不做自动合并算法（文本合并交给用户，不猜）
//
// 纳管范围（刻意小）：笔记 创建/更新、剪藏 创建。删除/批量/同步协议不接
// （删除走回收站可恢复，冲突代价低；批量导入请在线做）。
import api from '@/api/client';

export interface OutboxOp {
  key: string;            // 幂等键（uuid）
  ts: number;
  method: 'POST' | 'PUT';
  path: string;           // /api/v1/notes/ 或 /api/v1/notes/{id}
  body: Record<string, unknown>;
  baseUpdatedAt?: string; // 更新类的基线版本（编辑开始时拿到的 updated_at）
  meta: { kind: 'note-create' | 'note-update' | 'clip-create'; title: string };
}

export interface ConflictItem {
  key: string;
  ts: number;
  op: OutboxOp;
  /** 服务端当前行（409 时带回） */
  server: { title: string; content: string; updated_at: string } | null;
  error?: string;         // 非 409 的失败（400/403 等）如实记
}

const DB_NAME = 'molore-outbox';
const OPS = 'ops';
const CONFLICTS = 'conflicts';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(OPS, { keyPath: 'key' });
        req.result.createObjectStore(CONFLICTS, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const req = fn(s);
    t.oncomplete = () => resolve((req && 'result' in req ? req.result : undefined) as T);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

function notifyChange() {
  window.dispatchEvent(new CustomEvent('molore-outbox-change'));
}

/** 入队（断网拦截点调用）。key 即幂等键，重放安全。 */
export async function enqueue(op: Omit<OutboxOp, 'ts'>): Promise<void> {
  await tx(OPS, 'readwrite', (s) => s.put({ ...op, ts: Date.now() }));
  notifyChange();
}

export async function pendingCount(): Promise<number> {
  return tx(OPS, 'readonly', (s) => s.count());
}

export async function listConflicts(): Promise<ConflictItem[]> {
  const all = await tx(CONFLICTS, 'readonly', (s) => s.getAll());
  return (all as unknown as ConflictItem[]).sort((a, b) => b.ts - a.ts);
}

async function deleteOp(key: string): Promise<void> {
  await tx(OPS, 'readwrite', (s) => s.delete(key));
  notifyChange();
}

async function moveToConflict(op: OutboxOp, server: ConflictItem['server'], error?: string): Promise<void> {
  await tx(CONFLICTS, 'readwrite', (s) => s.put({ key: op.key, ts: Date.now(), op, server, error }));
  await deleteOp(op.key);
}

export async function resolveConflict(key: string): Promise<void> {
  await tx(CONFLICTS, 'readwrite', (s) => s.delete(key));
  notifyChange();
}

/** 冲突处理「用我的」：不带基线强写（用户已看过云端版仍选本地），成功后销冲突。 */
export async function resolveConflictMine(item: ConflictItem): Promise<void> {
  await api.request({
    url: item.op.path, method: item.op.method, data: item.op.body,
    headers: { 'X-Idempotency-Key': `${item.key}-force` },
  });
  await resolveConflict(item.key);
}

/** 冲突处理「手动合并」：正文取合并文本、基线取服务端当前版本（合并期间又被改会
    再撞 409 回冲突仓——如实不藏）。 */
export async function resolveConflictMerged(item: ConflictItem, mergedContent: string): Promise<void> {
  const fresh = await api.get(item.op.path);
  const base = fresh.data?.updated_at as string | undefined;
  await api.request({
    url: item.op.path, method: item.op.method,
    data: { ...item.op.body, content: mergedContent },
    headers: {
      'X-Idempotency-Key': `${item.key}-merged`,
      ...(base ? { 'X-Base-Updated-At': base } : {}),
    },
  });
  await resolveConflict(item.key);
}

let replaying = false;

/** 重放一轮：FIFO；网络断即停；409 移冲突仓；其余 4xx 也移冲突仓（如实记错误）。 */
export async function replayOutbox(): Promise<void> {
  if (replaying || !navigator.onLine) return;
  replaying = true;
  try {
    const ops = (await tx(OPS, 'readonly', (s) => s.getAll())) as unknown as OutboxOp[];
    ops.sort((a, b) => a.ts - b.ts);
    for (const op of ops) {
      const headers: Record<string, string> = { 'X-Idempotency-Key': op.key };
      if (op.baseUpdatedAt) headers['X-Base-Updated-At'] = op.baseUpdatedAt;
      try {
        await api.request({ url: op.path, method: op.method, data: op.body, headers });
        await deleteOp(op.key);
      } catch (err: any) {
        const status = err?.response?.status;
        if (!status) return; // 网络又断了：整轮停下，下轮再来
        if (status === 409) {
          await moveToConflict(op, err.response?.data?.current ?? null);
        } else if (status >= 400 && status < 500) {
          const data = err.response?.data as { detail?: string; error?: { message?: string } } | undefined;
          await moveToConflict(op, null, data?.detail || data?.error?.message || `HTTP ${status}`);
        } else {
          return; // 5xx：停下下轮重试
        }
      }
    }
  } finally {
    replaying = false;
    notifyChange();
  }
}

let booted = false;

/** 挂监听（App 启动调一次）：online 事件 + 30s 轮询 + 立即一轮。 */
export function bootOfflineOutbox(): void {
  if (booted) return;
  booted = true;
  window.addEventListener('online', () => { void replayOutbox(); });
  setInterval(() => { void replayOutbox(); }, 30_000);
  void replayOutbox();
}

/** 断网判定：navigator.onLine + 真实请求失败（axios 无响应体）。 */
export function isNetworkFailure(err: unknown): boolean {
  if (!navigator.onLine) return true;
  return !!(err && typeof err === 'object' && !('response' in (err as object)));
}
